// ─────────────────────────────────────────────────────────────────
// Glass-i — Proforma "Customer Salesperson" field (Stage SALES-PROFORMA-1).
//
// Compact, additive block for the Proforma customer area. On customer
// select it auto-loads the HRMS-mapped salesperson (read-only source of
// truth = customers.salesperson_employee_id). If none is mapped it shows
// a clear warning. A manual override picker (active HR salespersons only)
// requires a reason. It NEVER edits the customer mapping — only the
// proforma header. This is NOT the old user-based "Sales Representative".
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, Input, Tag, Alert, Spin } from 'antd';
import { proformaApi, loadSalespersonsSafe } from './proformaApi';
import { formatSalespersonSource, clearOverrideOnCustomerChange } from './proformaHelpers';

function sourceColor(source) {
  return ({ CUSTOMER_MAPPING: 'green', MANUAL_OVERRIDE: 'gold', NONE: 'default' })[source] || 'default';
}

export default function ProformaSalespersonField({ header, setHeader, readOnly, embedded, bare, onUserEdit }) {
  const [mapRes, setMapRes] = useState(null);   // server resolution of the pure customer mapping
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  const customerId = header.customerId || null;
  const source = header.salespersonSource || 'NONE';
  const selectedId = header.salespersonEmployeeId || null;
  const mappedId = mapRes && mapRes.source === 'CUSTOMER_MAPPING' ? mapRes.salespersonEmployeeId : null;

  // Load the active-salesperson options once (for the override picker).
  useEffect(() => {
    let alive = true;
    loadSalespersonsSafe().then(({ rows }) => {
      if (!alive) return;
      setOptions(rows.map((r) => ({
        value: r.id,
        label: `${r.displayName || r.employeeName}${r.employeeCode ? ` (${r.employeeCode})` : ''}`
      })));
    });
    return () => { alive = false; };
  }, []);

  // Resolve the customer's mapped salesperson whenever the customer changes.
  // The previous customer id is tracked so we can tell a REAL user switch
  // (A → B) apart from the initial load / same-customer re-resolve.
  const prevCustomerRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const prevCustomer = prevCustomerRef.current;
    prevCustomerRef.current = customerId;
    if (!customerId) { setMapRes(null); return () => { alive = false; }; }
    // Fix 3 (SALES-PROFORMA-AUDIT-FIX-1) — on a genuine customer switch, drop any
    // manual override from the previous customer BEFORE the new mapping resolves,
    // so customer B never inherits customer A's salesperson. Initial load
    // (prevCustomer == null) and same-customer re-resolves preserve the header,
    // which keeps a reopened override intact (composes with Fix 2 hydration).
    setHeader((h) => clearOverrideOnCustomerChange(h, prevCustomer, customerId));
    setLoading(true);
    proformaApi.customerSalesperson(customerId)
      .then((data) => {
        if (!alive || !data) return;
        setMapRes(data);
        // Adopt the mapping as the default header salesperson ONLY when the
        // header is not (still) tracking a manual override. After a real switch
        // the override was already cleared above, so B adopts its own mapping.
        setHeader((h) => {
          if (h.salespersonSource === 'MANUAL_OVERRIDE') return h;
          return {
            ...h,
            salespersonEmployeeId: data.canProceed ? data.salespersonEmployeeId : null,
            salespersonSource: data.source,
            salespersonOverrideReason: null
          };
        });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [customerId, setHeader]);

  // Merge the mapped salesperson into the option list if the picker doesn't
  // already carry it (so it always renders by name).
  const mergedOptions = useMemo(() => {
    if (mapRes && mapRes.salespersonEmployeeId && !options.some((o) => o.value === mapRes.salespersonEmployeeId)) {
      return [{ value: mapRes.salespersonEmployeeId, label: `${mapRes.salespersonName}${mapRes.salespersonCode ? ` (${mapRes.salespersonCode})` : ''}` }, ...options];
    }
    return options;
  }, [options, mapRes]);

  const onPick = useCallback((empId) => {
    // Fix 4 — a manual salesperson pick is a genuine user edit → mark dirty.
    if (onUserEdit) onUserEdit();
    if (!empId) {
      setHeader((h) => ({ ...h, salespersonEmployeeId: null, salespersonSource: 'NONE', salespersonOverrideReason: null }));
      return;
    }
    if (empId === mappedId) {
      setHeader((h) => ({ ...h, salespersonEmployeeId: empId, salespersonSource: 'CUSTOMER_MAPPING', salespersonOverrideReason: null }));
    } else {
      setHeader((h) => ({ ...h, salespersonEmployeeId: empId, salespersonSource: 'MANUAL_OVERRIDE' }));
    }
  }, [mappedId, setHeader, onUserEdit]);

  const showNoMapWarning = !!customerId && source === 'NONE';
  const showMapStaleWarning = !!customerId && mapRes && mapRes.code === 'MAPPED_INVALID' && source !== 'MANUAL_OVERRIDE';
  const needReason = source === 'MANUAL_OVERRIDE' && !String(header.salespersonOverrideReason || '').trim();

  // The control (select + mapping tag), the mapping warning, and the override
  // reason — shared between the standalone card and the `bare` variant (used
  // inside the Details & Summary popup, where the popup supplies the label rail).
  const control = (
    <>
      <div className="flex items-center gap-2">
        {!bare ? (
          <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap">
            Salesperson<span className="text-red-500"> *</span>
          </span>
        ) : null}
        <Spin spinning={loading} size="small" />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={customerId ? 'Select salesperson…' : 'Select a customer first'}
          value={selectedId || undefined}
          options={mergedOptions}
          disabled={readOnly || !customerId}
          onChange={onPick}
          style={{ minWidth: 180, flex: 1 }}
        />
        <Tag color={sourceColor(source)} style={{ marginInlineEnd: 0 }}>{formatSalespersonSource(source)}</Tag>
      </div>

      {(showNoMapWarning || showMapStaleWarning) ? (
        <Alert
          className="mt-2"
          type="warning"
          showIcon
          message={showMapStaleWarning
            ? (mapRes.warning || 'The salesperson mapped to this customer is no longer valid. Pick a manual override.')
            : 'No salesperson mapped for this customer. Pick a manual override to continue.'}
        />
      ) : null}

      {source === 'MANUAL_OVERRIDE' ? (
        <div className="mt-2">
          <Input
            size="small"
            placeholder="Override reason (required)"
            status={needReason ? 'error' : ''}
            disabled={readOnly}
            value={header.salespersonOverrideReason || ''}
            onChange={(e) => { if (onUserEdit) onUserEdit(); setHeader((h) => ({ ...h, salespersonOverrideReason: e.target.value })); }}
          />
          {needReason ? <div className="text-[12px] text-red-500 mt-1">A reason is required for a manual override.</div> : null}
        </div>
      ) : null}
    </>
  );

  // Bare: caller owns the container + label (popup rail). Otherwise the
  // self-contained card used elsewhere.
  if (bare) return control;
  return (
    <div className={`${embedded ? '' : 'mt-2 '}rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2`}>
      {control}
    </div>
  );
}
